// Customizable Hiring Flow (#3066 / #3070) — FE mirror of the BE contract
// (ourteam-backend #3639 data model + #3641 validation). The flow travels in the
// job payload as `hiring_flow` (POST /jobs/post, PUT /jobs/{id}) and comes back
// resolved on the job read together with `flow_version` +
// `hiring_flow_in_flight_count`.

export type HiringFlowStepType =
    | "form"
    | "ai_interview"
    | "eligibility_check"
    | "external_link"
    | "documents"
    | "human_interview"
    // #3257 (BE ourteam-backend#3996): HR-written WhatsApp message sent to the
    // candidate mid-flow; whatsapp_first only, no gates, always auto-advance.
    | "custom_message";

export type HiringFlowAdvance = "auto" | "review";
export type HiringFlowChannel = "web" | "whatsapp_first";
export type KnockoutOperator = "lt" | "lte" | "eq" | "neq" | "gt" | "gte";

export interface AnswerKnockoutGate {
    type: "answer_knockout";
    question_ref: string;
    operator: KnockoutOperator;
    value: string;
    // Gates never silently reject — flag & pause is the default; auto-reject is
    // a per-gate opt-in (off by default) per the epic guardrails.
    on_fail?: { action: "flag" };
    // Phase 2 (#3086, BE #3662): optional reroute target — HR resolving the
    // flag can copy the application to this job. Validated at save (exists,
    // same company, not closed).
    suggest_job_id?: string;
}

export interface DeadlineGate {
    type: "deadline";
    due: string; // e.g. "same_day_17:00"
    reminders?: string[]; // e.g. ["-4h", "-1h"]
    on_miss?: { action: "hold"; auto_reject: boolean };
}

export type HiringFlowGate = AnswerKnockoutGate | DeadlineGate;

export interface HiringFlowStep {
    id: string;
    type: HiringFlowStepType;
    advance?: HiringFlowAdvance; // default "auto"; the final step always ends in Ready for Review
    gate?: HiringFlowGate; // legacy single gate — kept for back-compat (current BE reads this)
    // #3236 (BE ourteam-backend#3976): MULTIPLE conditions per step. Same per-gate
    // shape as `gate`; OR semantics (flag when ANY knockout matches); ≤1 deadline
    // per step. The FE writes BOTH `gates` and `gate` (= first entry) so the
    // current single-gate BE keeps working until #3976 lands; on read, a legacy
    // flow carrying only `gate` is treated as a 1-item list (see stepGates).
    gates?: HiringFlowGate[];
    // form + ai_interview (ai_interview may omit it — BE falls back to the job's
    // interview assessment; form REQUIRES it)
    assessment_id?: string;
    delivery?: string[]; // form: ["whatsapp","web"]
    // ai_interview
    mode?: string; // "wa_call" | "walk_in_or_online" (human_interview)
    // #3248 (BE #3990): wa_call web-link fallback is now an HR opt-in per step —
    // "web" = ON (Maya sends the web assessment link when the call goes
    // unanswered), null = OFF (explicit, the BE contract's off value).
    fallback?: string | null;
    // external_link
    label?: string;
    url?: string;
    completion?: string; // "candidate_confirm" (default)
    // documents
    items?: string[];
    // custom_message (#3257, BE ourteam-backend#3996): the WhatsApp text sent to
    // the candidate when the flow reaches this step. Required non-empty; the
    // step fires and auto-advances (no gate, no review).
    message?: string;
    // eligibility_check (#3161): the selected work_authorization_metadata keys.
    // Carried in templates so the screening-field selection round-trips (BE #3813);
    // stripped from the live job flow — the job payload keeps it in
    // work_authorization_metadata, not on the step.
    fields?: string[];
}

export interface HiringFlowEntry {
    auto_start: boolean;
    cv_required_at_apply: boolean;
}

export interface HiringFlow {
    version: number;
    template: string; // provenance only; "ourteam_default" when untouched
    channel: HiringFlowChannel;
    entry: HiringFlowEntry;
    steps: HiringFlowStep[];
    // Chat-native WhatsApp flow (#3090, BE epic ourteam-backend#3691, flag #3697).
    // When true + channel="whatsapp_first", Maya runs the whole flow in the
    // WhatsApp thread (no link-out). Default off; only meaningful on
    // whatsapp_first. FE-proposed field name — see the BE handoff on #3697.
    chat_native?: boolean;
}

// ai_interview delivery modes. "web" = the default (mode omitted): web voice/
// video. "wa_call" = WhatsApp voice phone call. "chat" = text interview run by
// Maya inside the WhatsApp thread (#3090 / BE ourteam-backend#3696).
export type InterviewMode = "web" | "wa_call" | "chat";

// Guardrails (mockup §1 / BE #3641)
export const MAX_FLOW_STEPS = 8;

// Document Collection preset registry (#3098) — FE mirror of the BE
// DOCUMENT_TEMPLATE (ourteam-backend#3708). The builder stores the canonical
// `key` (matched by the WhatsApp document runner + CV detection); the `label`
// is what HR sees. `is_cv` marks the item that drives deferred CV parsing.
// Swap to a BE endpoint once #3708 exposes one — keep the keys identical.
export interface DocumentPreset {
    key: string;
    label: string;
    is_cv?: boolean;
}

export const DOCUMENT_TEMPLATE: DocumentPreset[] = [
    { key: "resume", label: "Resume / CV", is_cv: true },
    { key: "ic_front", label: "IC — front side" },
    { key: "ic_back", label: "IC — back side" },
    { key: "passport_photo", label: "Passport photo" },
    { key: "education_cert", label: "Education certificate" },
    { key: "typing_test", label: "Typing test" },
    { key: "medical_report", label: "Medical report" },
    { key: "background_check_form", label: "Background check form" },
    { key: "pdpa_consent", label: "PDPA consent" },
    { key: "job_application_form", label: "Job application form" },
    { key: "contractor_card_form", label: "Contractor card form" },
];

/** Sentinel Select value for the free-text "Custom…" escape hatch. */
export const DOCUMENT_CUSTOM = "__custom__";

/** Friendly label for a stored document key — preset label, else humanized. */
export function documentLabel(key: string): string {
    const preset = DOCUMENT_TEMPLATE.find((d) => d.key === key);
    if (preset) return preset.label;
    return key
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase());
}

export const STEP_TYPE_LABELS: Record<HiringFlowStepType, string> = {
    form: "Form / Questionnaire",
    ai_interview: "AI Interview",
    eligibility_check: "Eligibility Check",
    external_link: "External Link",
    documents: "Document Collection",
    human_interview: "Human Interview",
    custom_message: "Custom Message",
};

/**
 * Knockout operator wording, shared so the builder and the job Version History
 * describe the same condition identically ("is above", not "gt").
 */
export const KNOCKOUT_OPERATOR_LABELS: Record<KnockoutOperator, string> = {
    lt: "is below",
    lte: "is at most",
    eq: "equals",
    neq: "does not equal",
    gt: "is above",
    gte: "is at least",
};

/** Eligibility-question labels for a gate's `question_ref`, shared for the same reason. */
export const ELIGIBILITY_FIELD_LABELS: Record<string, string> = {
    collect_employment_status: "Current employment status",
    collect_expected_salary: "Expected salary",
    collect_start_availability: "Earliest available start date",
    collect_current_salary: "Current or last drawn salary",
    collect_relocation_willingness: "Willingness to relocate",
    collect_weekend_shift_willingness: "Availability for weekends or shift work",
    collect_work_authorization: "Work authorization status",
    collect_education_level: "Highest education level",
};

/**
 * One condition in HR's words — "Knock out when Expected salary is above 2400".
 * A form question's `question_ref` is a generated id (`q_1785419532194`), so it
 * reads as "a form answer" rather than exposing the raw id.
 */
export function describeGate(gate: HiringFlowGate): string {
    if (gate.type === "deadline") {
        return `Deadline${gate.due ? `: ${gate.due}` : ""}`;
    }
    const question = ELIGIBILITY_FIELD_LABELS[gate.question_ref] || "a form answer";
    const operator = KNOCKOUT_OPERATOR_LABELS[gate.operator] || gate.operator;
    return `Knock out when ${question} ${operator} ${gate.value}`;
}

export const STEP_TYPE_DESCRIPTIONS: Record<HiringFlowStepType, string> = {
    form: "Existing form builder · web or in-chat",
    ai_interview: "Web voice/video or WhatsApp phone call",
    eligibility_check: "Employment & availability details",
    external_link: "Send candidates to an external form or page",
    documents: "Collect a checklist of required documents",
    human_interview: "Schedule a walk-in or online interview",
    custom_message: "Send a WhatsApp message to the candidate",
};

let stepIdCounter = 0;
/** Unique step id within a flow ("s1", "s2", …) — unique against existing ids. */
export function nextStepId(existing: HiringFlowStep[]): string {
    const used = new Set(existing.map((s) => s.id));
    let id = "";
    do {
        stepIdCounter += 1;
        id = `s${stepIdCounter}`;
    } while (used.has(id));
    return id;
}

/** A new step of the given type with the contract's defaults. */
export function createStep(type: HiringFlowStepType, existing: HiringFlowStep[]): HiringFlowStep {
    const base: HiringFlowStep = { id: nextStepId(existing), type, advance: "auto" };
    if (type === "external_link") {
        base.completion = "candidate_confirm";
    }
    if (type === "documents") {
        base.items = ["resume"]; // canonical key (#3098) — the CV-driving preset
    }
    if (type === "human_interview") {
        base.mode = "walk_in_or_online";
    }
    if (type === "custom_message") {
        base.message = "";
    }
    return base;
}

/**
 * The step's conditions as a list (#3236). A legacy step carrying only the
 * singular `gate` reads as a 1-item list; `gates` (BE #3976) wins when present.
 */
export function stepGates(step: HiringFlowStep): HiringFlowGate[] {
    if (Array.isArray(step.gates) && step.gates.length > 0) return step.gates;
    return step.gate ? [step.gate] : [];
}

/**
 * Step patch that writes a conditions list back onto a step (#3236). Writes BOTH
 * shapes: `gates` (the #3976 contract) and `gate` = first entry, so the current
 * single-gate BE keeps enforcing the first condition until #3976 is live. An
 * empty list clears both (JSON.stringify drops the undefineds from the payload).
 */
export function gatesPatch(gates: HiringFlowGate[]): Pick<HiringFlowStep, "gate" | "gates"> {
    return {
        gate: gates.length > 0 ? gates[0] : undefined,
        gates: gates.length > 0 ? gates : undefined,
    };
}

// BE #3996: custom_message length cap — save 400s above this.
export const MAX_CUSTOM_MESSAGE_LENGTH = 1500;

/**
 * Mirror of the BE's save-time normalization (#3996): the BE trims each
 * custom_message text, so trim client-side too — otherwise a save→reload
 * round-trip differs and reads as a spurious unsaved-changes diff.
 */
export function normalizeFlowForSave(flow: HiringFlow): HiringFlow {
    return {
        ...flow,
        steps: flow.steps.map((s) =>
            s.type === "custom_message" && typeof s.message === "string" && s.message !== s.message.trim()
                ? { ...s, message: s.message.trim() }
                : s,
        ),
    };
}

/**
 * The ourteam Default flow — one AI Interview step, exactly today's effective
 * default. A job saved untouched stores `template:"ourteam_default"` and
 * behaves exactly like today (BE resolves `hiring_flow = NULL` to this).
 */
export function defaultHiringFlow(): HiringFlow {
    return {
        version: 1,
        template: "ourteam_default",
        channel: "web",
        entry: { auto_start: false, cv_required_at_apply: true },
        steps: [{ id: "s1", type: "ai_interview", advance: "auto" }],
    };
}

/** Version-independent content — mirrors the BE's `flow_content` used to decide version bumps. */
export function flowContent(flow: HiringFlow | null | undefined): Omit<HiringFlow, "version"> | null {
    if (!flow) return null;
    const { version: _version, ...content } = flow;
    return content;
}

export function flowsEqual(a: HiringFlow | null | undefined, b: HiringFlow | null | undefined): boolean {
    return JSON.stringify(flowContent(a)) === JSON.stringify(flowContent(b));
}

export interface FlowCheckItem {
    ok: boolean;
    message: string;
    /** A non-blocking advisory (e.g. duplicate assessment #3174) — shown in the
     *  Flow check panel but ignored by isFlowValid (doesn't block save/Next). */
    warning?: boolean;
}

/**
 * Steps that share the same assessment_id (Form / AI Interview) — the candidate
 * would be asked the identical questions twice (#3174). Returns a map of
 * assessment_id → the step indices using it, keeping only the duplicated ones.
 */
export function duplicateAssessmentGroups(steps: HiringFlowStep[]): Map<string, number[]> {
    const byAssessment = new Map<string, number[]>();
    steps.forEach((s, i) => {
        if ((s.type === "form" || s.type === "ai_interview") && s.assessment_id) {
            const arr = byAssessment.get(s.assessment_id) ?? [];
            arr.push(i);
            byAssessment.set(s.assessment_id, arr);
        }
    });
    const dups = new Map<string, number[]>();
    byAssessment.forEach((idxs, aid) => {
        if (idxs.length > 1) dups.set(aid, idxs);
    });
    return dups;
}

/**
 * Does this document key satisfy the CV / resume requirement (#3147)? Matches the
 * is_cv preset ("resume") plus common custom spellings (cv / resume / curriculum).
 */
export function isCvDocumentItem(key: string): boolean {
    const trimmed = (key || "").trim();
    if (!trimmed) return false;
    const preset = DOCUMENT_TEMPLATE.find((d) => d.key === trimmed);
    if (preset?.is_cv) return true;
    return /(^|[_\-\s])(cv|resume)([_\-\s]|$)|curriculum/i.test(trimmed);
}

/**
 * FE mirror of the BE save-time validation (#3641) so the Flow check panel can
 * block Next before the request: ≥1 ai_interview (web channels — for
 * whatsapp_first it's a WARNING only, BE #3976: screening may run fully
 * in-chat) · ≤8 steps · form needs an assessment · external_link needs a
 * URL · documents needs ≥1 item · knockout gates need question/operator/value ·
 * deadline gates need a due · per step: any number of knockouts, ≤1 deadline.
 */
export function checkHiringFlow(flow: HiringFlow, opts?: { waCallAvailable?: boolean; companyWhatsappEnabled?: boolean }): FlowCheckItem[] {
    const items: FlowCheckItem[] = [];
    const steps = flow.steps || [];

    const hasAiInterview = steps.some((s) => s.type === "ai_interview");
    // BE re-applies the >=1-AI-interview requirement when a whatsapp_first flow is
    // coerced to web (company WhatsApp off) — so the warning only applies when the
    // flow will actually run WhatsApp-first.
    if (!hasAiInterview && flow.channel === "whatsapp_first" && opts?.companyWhatsappEnabled !== false) {
        // BE #3976: a whatsapp_first flow may screen candidates fully in-chat —
        // non-blocking warning instead of the error. (The BE re-applies the
        // requirement if the flow gets coerced to web because company WhatsApp
        // is off.)
        items.push({
            ok: false,
            warning: true,
            message: "No AI Interview step — this WhatsApp-first flow will screen candidates without an AI interview",
        });
    } else {
        items.push({
            ok: hasAiInterview,
            message: "At least 1 AI Interview step is required",
        });
    }
    items.push({
        ok: steps.length >= 1 && steps.length <= MAX_FLOW_STEPS,
        message: `Between 1 and ${MAX_FLOW_STEPS} steps (currently ${steps.length})`,
    });

    steps.forEach((step, i) => {
        const label = `Step ${i + 1} · ${STEP_TYPE_LABELS[step.type]}`;
        if (step.type === "form" && !step.assessment_id) {
            items.push({ ok: false, message: `${label}: select a form assessment` });
        }
        if (step.type === "ai_interview" && !step.assessment_id) {
            items.push({ ok: false, message: `${label}: select an interview` });
        }
        if (step.type === "external_link" && !(step.url || "").trim()) {
            items.push({ ok: false, message: `${label}: a URL is required` });
        }
        if (step.type === "documents" && (!step.items || step.items.filter((it) => it.trim()).length === 0)) {
            items.push({ ok: false, message: `${label}: add at least 1 checklist item` });
        }
        if (step.type === "ai_interview" && step.mode === "wa_call" && opts?.waCallAvailable === false) {
            items.push({
                ok: false,
                message: `${label}: phone call needs Maya enabled — web fallback will be used`,
            });
        }
        // custom_message (#3257, BE #3996): whatsapp_first only · message required ·
        // no conditions (the editor hides them; this guards loaded/imported flows).
        if (step.type === "custom_message") {
            if (!(step.message || "").trim()) {
                items.push({ ok: false, message: `${label}: write the message to send` });
            }
            if ((step.message || "").length > MAX_CUSTOM_MESSAGE_LENGTH) {
                items.push({ ok: false, message: `${label}: message is too long (${(step.message || "").length}/${MAX_CUSTOM_MESSAGE_LENGTH} characters)` });
            }
            if (flow.channel !== "whatsapp_first") {
                items.push({ ok: false, message: `${label}: requires the WhatsApp-first channel — remove the step or switch the channel` });
            } else if (opts?.companyWhatsappEnabled === false) {
                // BE coerces the flow to web when company WhatsApp is off, then
                // rejects the step — surface that before the 400.
                items.push({ ok: false, message: `${label}: requires company WhatsApp to be enabled — with WhatsApp off this flow runs on the web channel` });
            }
            if (stepGates(step).length > 0) {
                items.push({ ok: false, message: `${label}: conditions aren't supported on this step` });
            }
        }
        // #3236: a step can carry multiple conditions (gates[]); validate each.
        const gates = stepGates(step);
        gates.forEach((gate, gi) => {
            const cond = gates.length > 1 ? `condition ${gi + 1}` : "condition";
            if (gate.type === "answer_knockout") {
                if (!gate.question_ref || !gate.operator || gate.value === undefined || gate.value === "") {
                    items.push({ ok: false, message: `${label}: knockout ${cond} needs a question, operator and value` });
                }
            } else if (gate.type === "deadline" && !(gate.due || "").trim()) {
                items.push({ ok: false, message: `${label}: deadline ${cond} needs a due time` });
            }
        });
        if (gates.filter((g) => g.type === "deadline").length > 1) {
            items.push({ ok: false, message: `${label}: at most one deadline condition per step` });
        }
        // #3236: identical knockout conditions on one step are always a mistake —
        // same question + operator + value (trimmed, case-insensitive value). Blank
        // entries are skipped; the needs-question/operator/value check covers them.
        const seenConditions = new Map<string, number>();
        gates.forEach((gate, gi) => {
            if (gate.type !== "answer_knockout" || !(gate.question_ref || "").trim()) return;
            const key = `${gate.question_ref.trim()}|${gate.operator}|${String(gate.value ?? "").trim().toLowerCase()}`;
            const first = seenConditions.get(key);
            if (first !== undefined) {
                items.push({ ok: false, message: `${label}: condition ${gi + 1} duplicates condition ${first + 1} — remove or change one` });
            } else {
                seenConditions.set(key, gi);
            }
        });
    });

    // #3174: non-blocking warning when two+ steps use the same assessment — the
    // candidate would be asked the identical questions twice.
    duplicateAssessmentGroups(steps).forEach((idxs) => {
        const first = idxs[0];
        idxs.slice(1).forEach((i) => {
            items.push({
                ok: false,
                warning: true,
                message: `Step ${i + 1} · ${STEP_TYPE_LABELS[steps[i].type]} repeats the assessment already used in Step ${first + 1}.`,
            });
        });
    });

    return items;
}

export function isFlowValid(flow: HiringFlow, opts?: { waCallAvailable?: boolean; companyWhatsappEnabled?: boolean }): boolean {
    // Warnings (wa_call web fallback, duplicate assessment #3174) don't block save.
    return checkHiringFlow(flow, opts)
        .filter((i) => !i.warning && !i.message.includes("web fallback"))
        .every((i) => i.ok);
}
